export const CONDITION_KEYS = ['condition', 'disable_when'];

const normalized = (value) => String(value || '').trim().toLowerCase();
const fieldSignature = (field) => [
  normalized(field.label),
  normalized(field.field_type),
  String(field.page || 1),
].join('|');

const mappedSignature = (field) => [
  normalized(field.bullhorn_source_field || field.mapped_bullhorn_field),
  normalized(field.field_type),
  String(field.page || 1),
].join('|');

function uniqueMatch(candidates, signature, signatureFn) {
  if (!signature || signature.startsWith('|')) return null;
  const matches = candidates.filter((field) => signatureFn(field) === signature);
  return matches.length === 1 ? matches[0] : null;
}

/**
 * Carry conditional rules across a replace-fields operation and rewrite their
 * source IDs to the corresponding replacement fields.
 */
export function reconcileFieldConditions(previousFields = [], replacementFields = []) {
  const byNewId = new Map(replacementFields.map((field) => [field.id, field]));
  const idMap = new Map();
  const claimedNewIds = new Set();

  for (const oldField of previousFields) {
    let match = byNewId.get(oldField.id);
    if (!match) match = uniqueMatch(replacementFields, fieldSignature(oldField), fieldSignature);
    if (!match) match = uniqueMatch(replacementFields, mappedSignature(oldField), mappedSignature);
    if (match && !claimedNewIds.has(match.id)) {
      idMap.set(oldField.id, match.id);
      claimedNewIds.add(match.id);
    }
  }

  let preserved = 0;
  let dropped = 0;
  const fields = replacementFields.map((newField) => {
    const oldEntry = [...idMap.entries()].find(([, newId]) => newId === newField.id);
    const oldField = oldEntry && previousFields.find((field) => field.id === oldEntry[0]);
    if (!oldField) return newField;
    const patch = {};
    for (const key of CONDITION_KEYS) {
      const rule = newField[key] || oldField[key];
      if (!rule?.field_id) continue;
      const nextSourceId = idMap.get(rule.field_id);
      if (nextSourceId && nextSourceId !== newField.id) {
        patch[key] = { ...rule, field_id: nextSourceId };
        preserved++;
      } else {
        patch[key] = null;
        dropped++;
      }
    }
    return Object.keys(patch).length ? { ...newField, ...patch } : newField;
  });

  return { fields, preserved, dropped };
}

/** Remove a field and explicitly clear every rule that depended on it. */
export function removeFieldAndConditionReferences(fields = [], fieldId) {
  let cleared = 0;
  const nextFields = fields
    .filter((field) => field.id !== fieldId)
    .map((field) => {
      const patch = {};
      for (const key of CONDITION_KEYS) {
        if (field[key]?.field_id === fieldId) {
          patch[key] = null;
          cleared++;
        }
      }
      return Object.keys(patch).length ? { ...field, ...patch } : field;
    });
  return { fields: nextFields, cleared };
}

export function findInvalidConditionReferences(fields = []) {
  const ids = new Set(fields.map((field) => field.id));
  const invalid = [];
  for (const field of fields) {
    for (const key of CONDITION_KEYS) {
      const rule = field[key];
      if (rule?.field_id && (!ids.has(rule.field_id) || rule.field_id === field.id)) {
        invalid.push({ field, key, sourceId: rule.field_id });
      }
    }
  }
  return invalid;
}
